// brenthelsnow: hide Colour / Size / Length filter chips that only match sold-out variants.
//
// Shopify builds facet values from every variant, sold-out included, so a chip can lead to "N products"
// and an empty grid. With the Search & Discovery "Availability" filter enabled, re-rendering the section
// with `filter.v.availability=1` makes Shopify combine colour/size/length with availability per variant,
// so a value that is absent or disabled in that response has no buyable variant. Lists opt in with
// `data-availability-gate="pending"` (snippets/list-filter.liquid); only the marked lists are touched.
// Fails open: any error, or the Availability filter being off, leaves every chip visible.
import { sectionRenderer } from '@theme/section-renderer';

const GATE_ATTR = 'data-availability-gate';
const UNBUYABLE_CLASS = 'facets__inputs-list-item--unbuyable';
const BREAK_CLASS = 'facets__inputs-list-break';
const EMPTY_BREAK_CLASS = 'facets__inputs-list-break--empty';
const AVAILABILITY_PARAM = 'filter.v.availability';
// Sale collections (only_sale_color_cards) also require a discount — list-filter marks their lists with
// data-sale-gate and the refetch adds the S&D filter on the variant metafield custom.on_sale.
const SALE_GATE_ATTR = 'data-sale-gate';
const ON_SALE_PARAM = 'filter.v.m.custom.on_sale';

/** @type {WeakMap<Element, number>} */
const runTokens = new WeakMap();
/** Lists that have been resolved at least once: on re-render they keep their verdict while refetching. */
const gatedOnce = new WeakSet();
const FAIL_OPEN_MS = 3000;
let nextToken = 0;
let scheduled = false;
let lastPrefetchedUrl = '';

/**
 * @param {Element} element
 * @returns {string | null}
 */
function sectionIdOf(element) {
  const section = element.closest('.shopify-section');
  return section ? section.id.replace(/^shopify-section-/, '') : null;
}

/**
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function keyOf(input) {
  return `${input.name}=${input.value}`;
}

/**
 * @returns {URL}
 */
function availabilityUrl() {
  const url = new URL(window.location.href);
  url.searchParams.set(AVAILABILITY_PARAM, '1');
  if (document.querySelector(`[${SALE_GATE_ATTR}]`)) url.searchParams.set(ON_SALE_PARAM, '1');
  return url;
}

/**
 * Fetches the section with the availability filter applied and returns the still-buyable values,
 * or null when that cannot be determined (fail open).
 * @param {string} sectionId
 * @returns {Promise<Set<string> | null>}
 */
async function fetchBuyableValues(sectionId) {
  try {
    const html = await sectionRenderer.getSectionHTML(sectionId, true, availabilityUrl());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // No gated list at all means the response is not the facets markup (error page, or a zero-result
    // search that omits the filters block) — unknown, not "nothing is buyable".
    if (!doc.querySelector(`[${GATE_ATTR}]`)) return null;
    const buyable = new Set();
    for (const input of doc.querySelectorAll(`[${GATE_ATTR}] input[name]`)) {
      if (!(input instanceof HTMLInputElement) || input.disabled) continue;
      buyable.add(keyOf(input));
    }
    return buyable;
  } catch {
    return null;
  }
}

/**
 * Board Length pills are grouped by row breaks (list-filter.liquid); hide a break when the group before
 * it has no visible chip left, so gaps don't stack up.
 * @param {Element} list
 */
function collapseEmptyGroups(list) {
  let groupHasVisible = false;
  for (const item of list.children) {
    if (item.classList.contains(BREAK_CLASS)) {
      item.classList.toggle(EMPTY_BREAK_CLASS, !groupHasVisible);
      groupHasVisible = false;
    } else if (!item.classList.contains(UNBUYABLE_CLASS)) {
      groupHasVisible = true;
    }
  }
}

async function resolveGates() {
  scheduled = false;
  const lists = Array.from(document.querySelectorAll(`[${GATE_ATTR}="pending"]`));
  if (lists.length === 0) return;

  const token = ++nextToken;
  for (const list of lists) {
    runTokens.set(list, token);
    // After a re-render the items still carry the previous verdict (data-skip-node-update keeps them).
    // Keep it on screen while the new answer loads so the panel doesn't expand and collapse; the
    // timer below drops it if the fetch stalls, so the gate still fails open. First load stays hidden.
    if (gatedOnce.has(list)) list.setAttribute(GATE_ATTR, 'resolving');
  }
  const failOpen = setTimeout(() => {
    for (const list of lists) {
      if (runTokens.get(list) !== token) continue;
      for (const item of list.children) item.classList.remove(UNBUYABLE_CLASS);
      gatedOnce.add(list);
      list.setAttribute(GATE_ATTR, 'resolved');
    }
  }, FAIL_OPEN_MS);

  const sectionId = sectionIdOf(lists[0]);
  const buyable = sectionId ? await fetchBuyableValues(sectionId) : null;
  clearTimeout(failOpen);

  for (const list of lists) {
    if (!list.isConnected || runTokens.get(list) !== token) continue;
    gatedOnce.add(list);
    for (const item of list.children) {
      const input = item.querySelector('input[name]');
      let show = true;
      if (buyable && input instanceof HTMLInputElement) {
        show = input.checked || buyable.has(keyOf(input));
      }
      item.classList.toggle(UNBUYABLE_CLASS, !show);
    }
    collapseEmptyGroups(list);
    list.setAttribute(GATE_ATTR, 'resolved');
  }
}

function scheduleResolve() {
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(resolveGates);
}

// The facets section is morphed in place on every filter change; the server HTML brings the lists
// back as "pending", which is the signal to re-check against the new URL.
new MutationObserver(scheduleResolve).observe(document.body, {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: [GATE_ATTR],
});

// Facet clicks push the new URL synchronously and then render; start the availability request for
// that URL right away (getSectionHTML caches and shares in-flight fetches) so the answer is usually
// there by the time the morphed lists come back as "pending".
function prefetchForCurrentUrl() {
  setTimeout(() => {
    const list = document.querySelector(`[${GATE_ATTR}]`);
    const sectionId = list ? sectionIdOf(list) : null;
    if (!sectionId || window.location.href === lastPrefetchedUrl) return;
    lastPrefetchedUrl = window.location.href;
    sectionRenderer.getSectionHTML(sectionId, true, availabilityUrl()).catch(() => {});
  }, 0);
}
document.addEventListener('change', prefetchForCurrentUrl);
document.addEventListener('click', prefetchForCurrentUrl);

scheduleResolve();
